import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import Switch from './Switch';

/** Keep conflict/helper alerts aligned with the description column in this section. */
export const CONFIG_SWITCH_DESCRIPTION_MAX_WIDTH = '430px';

interface Props {
  title?: string;
  description?: string | React.ReactNode;
  renderSwitchLabel?: (isChecked: boolean) => React.ReactNode;
  isChecked: boolean;
  onToggle: (newValue: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
  switchLabelTitle?: string;
  plural?: boolean;
  switchTestId?: string;
}

export const ConfigSwitchSection = ({
  title,
  description,
  isChecked,
  onToggle,
  isLoading,
  disabled,
  switchLabelTitle,
  renderSwitchLabel,
  plural,
  switchTestId,
}: Props) => {
  const handleToggle = (newValue: boolean) => {
    onToggle(newValue);
  };

  return (
    <Stack component="section" spacing={1}>
      {title && (
        <Typography component="h2" variant="large" fontWeight="bold">
          {title}
        </Typography>
      )}
      {description && (
        <Box
          sx={{
            color: 'text.secondary',
            fontSize: '12px',
            maxWidth: CONFIG_SWITCH_DESCRIPTION_MAX_WIDTH,
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Box>
      )}
      {isLoading ? (
        <Box>
          <Skeleton sx={{ height: '24px', width: '250px' }} />
          <Skeleton sx={{ width: '50px', height: '44px', borderRadius: '16px' }} />
        </Box>
      ) : (
        <>
          <Typography
            sx={{
              mb: 0.5,
              '& span': { fontSize: '12px' },
            }}
          >
            {renderSwitchLabel ? (
              renderSwitchLabel(isChecked)
            ) : (
              <>
                <Typography component="span">{`${switchLabelTitle ?? title} ${plural ? 'are' : 'is'} currently `}</Typography>
                <Typography sx={{ fontWeight: 'bold' }} component="span">
                  {`${isChecked ? 'enabled' : 'disabled'}`}
                </Typography>
              </>
            )}
          </Typography>
          <Switch
            isChecked={isChecked}
            onToggle={() => handleToggle(!isChecked)}
            aria-label={`${title} Switch`}
            isDisabled={disabled}
            data-testid={switchTestId}
          />
        </>
      )}
    </Stack>
  );
};
